"use client";

import { redirect, usePathname, useRouter } from "next/navigation";
import React, { useEffect, useMemo, useState } from "react";
import { IconType } from "react-icons";
import { GoHome } from "react-icons/go";
import { IoSearch } from "react-icons/io5";
import { RxImage } from "react-icons/rx";
import { TbUser } from "react-icons/tb";
import { useAuthContext } from "../providers/AuthContextProvider";
import { getLabel } from "@/data/labels";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";
import { getLocalePrefix } from "@/utils/getLocaleFromPath";

interface MenuType {
  idx: number;
  name: string;
  logo: IconType;
  status: boolean;
  path: string;
}

const getOptions = (lang: AppLocale): MenuType[] => [
  { idx: 1, name: getLabel(lang, "HOME"), logo: GoHome, status: true, path: "/" },
  { idx: 2, name: getLabel(lang, "STORIES"), logo: RxImage, status: false, path: "/visual-stories" },
  { idx: 3, name: getLabel(lang, "SEARCH"), logo: IoSearch, status: false, path: "/search" },
  { idx: 4, name: getLabel(lang, "ME"), logo: TbUser, status: false, path: "/user/profile" },
];

const BottomNavigator = () => {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "te-IN";
  const lang = (locale?.split("-")[0] || "en") as AppLocale;
  const options = useMemo(() => getOptions(lang), [lang]);

  const [option, setOption] = useState<MenuType[]>(options);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const router = useRouter();
  const localePrefix = getLocalePrefix(pathname);

  const { user } = useAuthContext();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY) {
        // Scrolling down
        setIsVisible(false);
      } else {
        // Scrolling up
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);

  useEffect(() => {
    // Determine which option should be active based on the URL (locale-aware)
    const isLocaleRoot = pathname === "/" || pathname === localePrefix;
    const isStories = pathname.includes("visual-stories");
    const isUser = pathname.includes("/user");
    const isSearch = pathname.includes("/search");

    const updatedOptions = options?.map(data => {
      if (
        (isLocaleRoot && data.idx === 1) ||
        (isStories && data.idx === 2) ||
        (isUser && data.idx === 4) ||
        (isSearch && data.idx === 3)
      ) {
        return { ...data, status: true };
      } else if (!isStories && !isUser && !isSearch && data.idx === 1) {
        return { ...data, status: true };
      } else {
        return { ...data, status: false };
      }
    });

    setOption(updatedOptions);
  }, [pathname, localePrefix, options]);

  const handleClick = (id: number) => {
    const clickedOption = options?.find(data => data.idx === id);
    if (clickedOption) {
      if (clickedOption?.idx === 4 && !user) {
        router.push(localePrefix ? `${localePrefix}/register` : "/register");
      } else {
        const basePath = clickedOption.path;
        const targetPath = localePrefix
          ? `${localePrefix}${basePath === "/" ? "" : basePath}`
          : basePath;
        router.push(targetPath || "/");
      }
    }
  };
  return (
    <div
      className={`fixed z-50 block md:hidden bottom-[calc(1.25rem+env(safe-area-inset-bottom,0px))] w-full h-12 px-10 transition-transform duration-500 ${
        isVisible ? "translate-y-0" : "translate-y-[100px]"
      }`}
    >
      <div className="w-full h-full bg-[#333333]/70 rounded-full grid grid-cols-5 gap-2 px-2">
        {option?.map((item, index) => {
          return (
            <div
              key={index}
              onClick={() => handleClick(item?.idx)}
              className={` ${
                item?.status ? " col-span-2 " : ""
              } text-white flex justify-center items-center transition-all duration-1000 cursor-pointer`}
            >
              <div
                className={`flex justify-center items-center gap-1 transition-all duration-500 ${
                  item?.status ? " text-white bg-white/30 py-1 px-3 rounded-full " : ""
                }`}
              >
                <span className="">
                  <item.logo size={25} />
                </span>
                {item?.status ? (
                  <span className="text-sm font-semibold font-montserrat">{item?.name}</span>
                ) : (
                  ""
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BottomNavigator;
